import { create } from 'zustand';
import toast from 'react-hot-toast';
import { buildApiUrl, WS_URL } from '../config/api';

export const useExecutionStore = create((set, get) => ({
  // 状態
  executions: {},
  websocket: null,
  isConnected: false,
  reconnectTimer: null,
  manualDisconnect: false,
  hasShownRealtimeConnected: false,
  
  // WebSocket接続
  connectWebSocket: () => {
    try {
      const { websocket, isConnected, reconnectTimer } = get();
      if (
        isConnected ||
        (websocket && (websocket.readyState === WebSocket.OPEN || websocket.readyState === WebSocket.CONNECTING))
      ) {
        return;
      }

      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        set({ reconnectTimer: null });
      }

      const ws = new WebSocket(WS_URL);
      set({ manualDisconnect: false });
      
      ws.onopen = () => {
        console.log('WebSocket接続成功');
        set({ websocket: ws, isConnected: true });
        if (!get().hasShownRealtimeConnected) {
          toast.success('リアルタイム通信が有効になりました');
          set({ hasShownRealtimeConnected: true });
        }
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          get().handleWebSocketMessage(data);
        } catch (error) {
          console.error('WebSocketメッセージ解析エラー:', error);
        }
      };
      
      ws.onclose = (event) => {
        console.log('WebSocket接続切断');
        set({ websocket: null, isConnected: false });

        if (get().manualDisconnect) {
          return;
        }

        if (event.code === 1008 || event.code === 4001) {
          toast.error('ログイン状態が無効です。再ログインしてください');
          return;
        }
        
        // 再接続試行（5秒後）
        const timer = setTimeout(() => {
          if (!get().isConnected) {
            console.log('WebSocket再接続を試行...');
            get().connectWebSocket();
          }
        }, 5000);

        set({ reconnectTimer: timer });
      };
      
      ws.onerror = (error) => {
        console.error('WebSocketエラー:', error);
        toast.error('リアルタイム通信でエラーが発生しました');
      };
      
    } catch (error) {
      console.error('WebSocket接続エラー:', error);
      toast.error('WebSocket接続に失敗しました');
    }
  },

  disconnectWebSocket: () => {
    const { websocket, reconnectTimer } = get();

    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
    }

    if (websocket && websocket.readyState === WebSocket.OPEN) {
      websocket.close();
    }

    set({
      websocket: null,
      isConnected: false,
      reconnectTimer: null,
      manualDisconnect: true,
    });
  },
  
  // WebSocketメッセージ処理
  handleWebSocketMessage: (data) => {
    const { executions } = get();
    
    switch (data.type) {
      case 'progress':
        set({
          executions: {
            ...executions,
            [data.jobId]: {
              ...executions[data.jobId],
              progress: data.progress,
              status: 'running'
            }
          }
        });
        break;
        
      case 'completed':
        set({
          executions: {
            ...executions,
            [data.jobId]: {
              ...executions[data.jobId],
              status: 'completed',
              result: data.result,
              endTime: new Date().toISOString(),
              progress: { percentage: 100, message: '完了' }
            }
          }
        });
        toast.success(`ジョブ ${data.jobId} が完了しました`);
        break;
        
      case 'error':
        set({
          executions: {
            ...executions,
            [data.jobId]: {
              ...executions[data.jobId],
              status: 'failed',
              error: data.error,
              endTime: new Date().toISOString()
            }
          }
        });
        toast.error(`ジョブ ${data.jobId} でエラーが発生しました`);
        break;
      
      case 'workflow_progress':
        set({
          executions: {
            ...executions,
            [data.jobId]: {
              ...executions[data.jobId],
              status: data.status,
              progress: {
                percentage: data.percentage,
                message: data.message,
                currentLevel: data.currentLevel,
                totalLevels: data.totalLevels,
                currentNodes: data.currentNodes
              },
              results: data.results
            }
          }
        });
        
        if (data.status === 'completed') {
          toast.success(`ワークフロー ${data.jobId} が完了しました`);
        } else if (data.status === 'failed') {
          toast.error(`ワークフロー ${data.jobId} でエラーが発生しました`);
        }
        break;
      
      case 'node_status':
        // ノード単位の状態更新
        set({
          executions: {
            ...executions,
            [data.jobId]: {
              ...executions[data.jobId],
              nodeStatuses: {
                ...(executions[data.jobId]?.nodeStatuses || {}),
                [data.nodeId]: {
                  status: data.status,
                  message: data.message,
                  result: data.result,
                  timestamp: data.timestamp
                }
              }
            }
          }
        });
        break;
      
      case 'node_progress':
        // ノードの進捗更新
        set({
          executions: {
            ...executions,
            [data.jobId]: {
              ...executions[data.jobId],
              nodeProgresses: {
                ...(executions[data.jobId]?.nodeProgresses || {}),
                [data.nodeId]: data.progress
              }
            }
          }
        });
        break;
        
      default:
        console.log('未知のWebSocketメッセージ:', data);
    }
  },
  
  // ノード実行
  executeNode: async (nodeType, inputData) => {
    try {
      const response = await fetch(buildApiUrl('/ai-studio/v1/api/execute'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          nodeType,
          inputData
        })
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || '実行に失敗しました');
      }
      
      // 実行情報を状態に保存
      const { executions } = get();
      set({
        executions: {
          ...executions,
          [result.jobId]: {
            jobId: result.jobId,
            nodeType,
            inputData,
            status: 'started',
            startTime: new Date().toISOString(),
            progress: { percentage: 0, message: '開始中...' }
          }
        }
      });
      
      // WebSocketでジョブIDを登録
      const { websocket } = get();
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify({
          type: 'register',
          executionId: result.jobId
        }));
      }
      
      toast.success(`ジョブ ${result.jobId} を開始しました`);
      return result.jobId;
      
    } catch (error) {
      console.error('ノード実行エラー:', error);
      toast.error(`実行エラー: ${error.message}`);
      throw error;
    }
  },

  // ワークフロー全体を実行
  executeWorkflow: async (workflow, nodes, inputs = {}) => {
    try {
      const response = await fetch(buildApiUrl('/ai-studio/v1/execute'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workflow,
          nodes,
          inputs
        })
      });
      
      const result = await response.json();
      
      if (result.error) {
        throw new Error(result.error);
      }
      
      // 実行情報を状態に保存
      const { executions } = get();
      set({
        executions: {
          ...executions,
          [result.jobId]: {
            jobId: result.jobId,
            workflow,
            nodes,
            inputs,
            status: 'started',
            startTime: new Date().toISOString(),
            progress: { percentage: 0, message: 'ワークフロー開始中...' },
            nodeStatuses: {},
            nodeProgresses: {}
          }
        }
      });
      
      // WebSocketでジョブIDを登録
      const { websocket } = get();
      if (websocket && websocket.readyState === WebSocket.OPEN) {
        websocket.send(JSON.stringify({
          type: 'register',
          executionId: result.jobId
        }));
      }
      
      toast.success(`ワークフロー実行を開始しました (Job: ${result.jobId})`);
      return result.jobId;
      
    } catch (error) {
      console.error('ワークフロー実行エラー:', error);
      toast.error(`実行エラー: ${error.message}`);
      throw error;
    }
  },
  
  // ジョブ状態確認
  checkJobStatus: async (jobId) => {
    try {
      const response = await fetch(buildApiUrl(`/ai-studio/v1/api/status/${jobId}`), {
        method: 'GET',
        credentials: 'include',
      });
      const result = await response.json();
      
      if (result.success && result.job) {
        const { executions } = get();
        set({
          executions: {
            ...executions,
            [jobId]: {
              ...executions[jobId],
              ...result.job
            }
          }
        });
        
        return result.job;
      } else {
        throw new Error(result.error || 'ジョブが見つかりません');
      }
    } catch (error) {
      console.error('ジョブ状態確認エラー:', error);
      return null;
    }
  },
  
  // 実行中ジョブの復元
  restoreExecutions: async () => {
    try {
      // ローカルストレージから実行情報を取得
      const projects = localStorage.getItem('ai-studio-projects');
      if (!projects) return;
      
      const projectList = JSON.parse(projects);
      const { executions } = get();
      
      for (const project of projectList) {
        if (project.executions) {
          for (const [, execution] of Object.entries(project.executions)) {
            if (execution.status === 'running' || execution.status === 'started') {
              // サーバーから最新状態を取得
              const status = await get().checkJobStatus(execution.jobId);
              
              if (status) {
                set({
                  executions: {
                    ...executions,
                    [execution.jobId]: {
                      ...execution,
                      ...status
                    }
                  }
                });
                
                // まだ実行中の場合はポーリング開始
                if (status.status === 'running') {
                  get().startPolling(execution.jobId);
                }
              } else {
                // サーバーで見つからない場合は不明状態に
                set({
                  executions: {
                    ...executions,
                    [execution.jobId]: {
                      ...execution,
                      status: 'unknown',
                      error: 'サーバー再起動により状態が不明です'
                    }
                  }
                });
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('実行状態復元エラー:', error);
    }
  },
  
  // ポーリング開始
  startPolling: (jobId) => {
    const pollInterval = setInterval(async () => {
      const status = await get().checkJobStatus(jobId);
      
      if (!status || status.status === 'completed' || status.status === 'failed') {
        clearInterval(pollInterval);
      }
    }, 2000); // 2秒間隔
    
    // ポーリングIDを保存（必要に応じて停止できるように）
    return pollInterval;
  },
  
  // ファイルアップロード
  uploadFile: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(buildApiUrl('/ai-studio/v1/api/upload'), {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || 'アップロードに失敗しました');
      }
      
      toast.success(`ファイル "${file.name}" をアップロードしました`);
      return result.file;
      
    } catch (error) {
      console.error('ファイルアップロードエラー:', error);
      toast.error(`アップロードエラー: ${error.message}`);
      throw error;
    }
  },
  
  // 実行履歴クリア
  clearExecution: (jobId) => {
    const { executions } = get();
    const { [jobId]: removed, ...remaining } = executions;
    set({ executions: remaining });
  },
  
  // 全実行履歴クリア
  clearAllExecutions: () => {
    set({ executions: {} });
    toast.success('実行履歴をクリアしました');
  },
  
  // 実行結果ダウンロード
  downloadResult: (jobId, result) => {
    try {
      const dataStr = JSON.stringify(result, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `result_${jobId}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('結果をダウンロードしました');
    } catch (error) {
      console.error('ダウンロードエラー:', error);
      toast.error('ダウンロードに失敗しました');
    }
  }
}));